import { cn } from '~/lib/cn';

type MeterTone = 'blue' | 'gold' | 'draft' | 'ink';

const fills: Record<MeterTone, string> = {
  blue: 'bg-blue',
  gold: 'bg-gold',
  draft: 'bg-draft-line',
  ink: 'bg-ink',
};

/**
 * A horizontal bar with a fill. `label` is read by screen readers ("64 percent of allowance used").
 * Gold only when a person needs to act (near or over an allowance).
 */
export function Meter({
  pct,
  tone = 'blue',
  height = 6,
  track = 'bg-sunk',
  label,
  className,
}: {
  pct: number;
  tone?: MeterTone;
  height?: 4 | 6 | 8 | 12 | 14;
  track?: string;
  label?: string;
  className?: string;
}) {
  const clamped = Math.max(0, Math.min(100, pct));
  const h = {
    4: 'h-1 rounded-[2px]',
    6: 'h-1.5 rounded-[3px]',
    8: 'h-2 rounded-[4px]',
    12: 'h-3 rounded-[6px]',
    14: 'h-3.5 rounded-[7px]',
  }[height];
  const fill = (
    <span className={cn(fills[tone], height >= 12 ? '' : h)} style={{ width: `${clamped}%` }} />
  );
  const cls = cn('flex overflow-hidden', h, track, className);
  return label ? (
    <span role="img" aria-label={label} className={cls}>
      {fill}
    </span>
  ) : (
    <span aria-hidden="true" className={cls}>
      {fill}
    </span>
  );
}

/** "$38.20 of $60" over a meter: allowance rows in Settings and the inventory. */
export function AllowanceMeter({
  name,
  usedLabel,
  pct,
  warn,
}: {
  name: string;
  usedLabel: string;
  pct: number;
  warn?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1 text-13">
      <span className="flex justify-between">
        <span>{name}</span>
        {warn ? <strong className="text-gold-ink">{usedLabel}</strong> : <span>{usedLabel}</span>}
      </span>
      <Meter
        pct={pct}
        tone={warn ? 'gold' : 'blue'}
        label={`${Math.round(pct)} percent of allowance used`}
      />
    </div>
  );
}
